import { useTranslation } from 'react-i18next';
import { useMaintenance } from '../hooks/useMaintenance';
import type { MaintenanceSection, MaintenanceSectionKey } from '../types/maintenance.types';
import { MaintenanceIcon } from './MaintenancePeriod';

/**
 * Heads-up strip shown above the page content while signed in: upcoming
 * (scheduled) maintenances for any section, plus sections currently blocked
 * (`medical_record` / `appointments`). A whole-app maintenance never reaches
 * this banner — MaintenanceGate replaces the app before that.
 */
export function MaintenanceBanner() {
  const { t } = useTranslation();
  const { section } = useMaintenance();

  const notices = (['all', 'medical_record', 'appointments'] as const)
    .map((key) => ({ key, section: section(key) }))
    .filter(({ section: s }) => s.status === 'scheduled' || s.status === 'active');

  if (notices.length === 0) return null;

  return (
    <div className="mb-5 flex flex-col gap-2">
      {notices.map(({ key, section: s }) => (
        <Notice key={key} sectionKey={key} section={s} label={t(`maintenance.sections.${key}`)} />
      ))}
    </div>
  );
}

function Notice({
  sectionKey,
  section,
  label,
}: {
  sectionKey: MaintenanceSectionKey;
  section: MaintenanceSection;
  label: string;
}) {
  const { t } = useTranslation();
  const active = section.status === 'active';

  return (
    <div
      role="status"
      className="flex items-start gap-3 rounded-card border border-warning/40 bg-warning-light px-4 py-3 text-sm text-ink"
    >
      <span className="mt-0.5 shrink-0 text-[#9a6700]">
        <MaintenanceIcon className="h-5 w-5" />
      </span>
      <p className="whitespace-pre-line">
        <span className="font-semibold">
          {active ? t('maintenance.active_label') : t('maintenance.scheduled_label')}
          {sectionKey !== 'all' && ` · ${label}`}:
        </span>{' '}
        {section.message ?? t('maintenance.default_message')}
      </p>
    </div>
  );
}
